import React, { Component, createRef } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CircularProgress,
  Popover,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Badge,
  Chip,
  Typography,
  LinearProgress,
} from "@mui/material";
import { Menu as MenuIcon, Notifications as NotificationsIcon, Bed as BedIcon, Image as ImageIcon, Wifi as WifiIcon, WifiOff as WifiOffIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import Notifications from "./Notifications";
import { Link } from "react-router-dom";
import LogoIcon from "../../media/shieldshot_logo.png";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { API_URL, TEMP_BUCKET } from "../../config";
import { NotificationsApi, AnalyzeApi } from "../../services/api/misc";

class NavBar extends Component {
  intervalID = null;
  deb = 0;
  lastActivityTime = Date.now();
  activityCheckInterval = null;
  tokenCheckInterval = null;
  isPollingPaused = false;
  authRefreshTimeout = null;
  lastAuthRefresh = 0;

  constructor(props) {
    super(props);

    const navItems = NavBar.getNavBarItems(props.accountType).map((item, idx) => ({
      ...item,
      active: idx === 0,
    }));

    this.state = {
      sidebars: navItems,
      link: this.props.isRegistered ? "/dashboard" : "/register",
      uploadPhotoToAnalyze: false,
      drawerOpen: false,
      notificationAnchorEl: null,
      notificationNumber: 0,
      notificationLoading: true,
      totalNotifications: -1,
      notifications: [],
      wsConnection: null,
      wsStatus: 'disconnected',
      useWebSocket: true, // Feature flag for WebSocket usage
    };
  }

  componentDidMount = () => {
    if (localStorage.getItem("notifications")) {
      this.setState({
        notifications: JSON.parse(localStorage.getItem("notifications")),
        notificationNumber: parseInt(
          localStorage.getItem("notificationNumber")
        ),
        totalNotifications: parseInt(
          localStorage.getItem("totalNotifications")
        ),
        notificationLoading: false,
      });
    }

    this.initializeNotifications();
  };

  componentWillUnmount = () => {
    this.cleanupNotifications();
  };

  componentDidUpdate = (prevProps) => {
    // If token or active status changed, reinitialize notifications
    if (prevProps.token !== this.props.token || prevProps.active !== this.props.active) {
      console.log('Token or active status changed, reinitializing notifications');
      this.cleanupNotifications();
      // Add a small delay to ensure cleanup is complete before reconnecting
      setTimeout(() => {
        this.initializeNotifications();
      }, 100);
    }
  };

  initializeNotifications = () => {
    if (!this.props.active) {
      this.setState({ notificationLoading: false });
      return;
    }

    const token = this.props.token;
    if (!token || typeof token !== 'string' || token === 'undefined') {
      console.log('No valid token found for notifications.');
      this.setState({ notificationLoading: false });
      return;
    }

    // Try WebSocket first, fall back to HTTP polling
    if (this.state.useWebSocket) {
      this.connectWebSocket(token);
    } else {
      this.startHttpPolling(token);
    }
  };

  cleanupNotifications = () => {
    console.log('Cleaning up notifications...');

    // Cleanup WebSocket connection
    if (this.state.wsConnection) {
      this.state.wsConnection.disconnect();
      this.setState({ wsConnection: null, wsStatus: 'disconnected' });
    }

    // Cleanup HTTP polling
    if (this.intervalID) {
      clearInterval(this.intervalID);
      this.intervalID = null;
    }

    // Cleanup WebSocket timeout
    if (this.wsTimeout) {
      clearTimeout(this.wsTimeout);
      this.wsTimeout = null;
    }

    // Cleanup activity tracking
    this.cleanupActivityTracking();
  };

  connectWebSocket = (token) => {
    try {
      console.log('Connecting to WebSocket notifications...');

      // Ensure any existing connection is properly cleaned up
      if (this.state.wsConnection) {
        console.log('Cleaning up existing WebSocket connection before creating new one');
        this.state.wsConnection.disconnect();
      }

      const wsConnection = NotificationsApi.connectRealTime(
        token,
        this.handleWebSocketNotification,
        this.handleWebSocketConnection
      );

      this.setState({
        wsConnection,
        wsStatus: 'connecting',
        notificationLoading: false
      });

      // Set a timeout to fall back to HTTP if WebSocket fails to connect
      this.wsTimeout = setTimeout(() => {
        if (this.state.wsStatus === 'connecting') {
          console.log('WebSocket connection timeout, falling back to HTTP polling');
          this.fallbackToHttpPolling(token);
        }
      }, 10000); // Increased timeout to 10 seconds for better reliability

    } catch (error) {
      console.error('Failed to connect WebSocket, falling back to HTTP:', error);
      this.fallbackToHttpPolling(token);
    }
  };

  fallbackToHttpPolling = (token) => {
    console.log('Falling back to HTTP polling for notifications');

    // Clean up WebSocket connection and timeout
    if (this.state.wsConnection) {
      this.state.wsConnection.disconnect();
    }
    if (this.wsTimeout) {
      clearTimeout(this.wsTimeout);
      this.wsTimeout = null;
    }

    this.setState({
      wsConnection: null,
      wsStatus: 'disconnected',
      useWebSocket: false
    });

    this.startHttpPolling(token);
  };

  startHttpPolling = (token) => {
    console.log('Starting HTTP polling for notifications...');
    this.getNotifications();
    // Reduced frequency: poll every 30 seconds instead of 10 seconds
    this.intervalID = setInterval(this.getNotifications, 30000);

    // Track activity for intelligent polling
    this.initializeActivityTracking();
  };

  handleWebSocketNotification = (notification) => {
    console.log('Received WebSocket notification:', notification);

    // Add the new notification to the state
    this.setState(prevState => {
      const currentNotifications = prevState.notifications?.Items || [];
      let updatedItems = [notification, ...currentNotifications];

      // Apply same filtering logic as getNotifications
      const totalCount = updatedItems.length;
      if (totalCount > 5) {
        updatedItems = updatedItems.filter(item => !item.isRead);
        console.log(`WebSocket notification filtering: ${totalCount} -> ${updatedItems.length} (removed read notifications)`);
      }

      // Sort notifications by creation date (newest first)
      updatedItems.sort((a, b) => b.createdAt - a.createdAt);

      const newNotifications = {
        Items: updatedItems,
        Count: updatedItems.length
      };

      // Update localStorage
      localStorage.setItem("notifications", JSON.stringify(newNotifications));
      localStorage.setItem("totalNotifications", newNotifications.Count);

      // Calculate unread count
      const unreadCount = newNotifications.Items.filter(item => !item.isRead).length;
      localStorage.setItem("notificationNumber", unreadCount);

      return {
        notifications: newNotifications,
        notificationNumber: unreadCount,
        totalNotifications: newNotifications.Count
      };
    });
  };

  handleWebSocketConnection = (connectionData) => {
    console.log('WebSocket connection status:', connectionData);

    this.setState({ wsStatus: connectionData.status });

    // Clear timeout when we get a definitive status
    if (this.wsTimeout && (connectionData.status === 'connected' || connectionData.status === 'unavailable' || connectionData.status === 'error')) {
      clearTimeout(this.wsTimeout);
      this.wsTimeout = null;
    }

    if (connectionData.status === 'connected') {
      console.log('WebSocket connected successfully');
      // Load initial notifications via HTTP when WebSocket connects
      this.getNotifications();
    } else if (connectionData.status === 'unavailable') {
      // WebSocket not available, fall back to HTTP polling immediately
      console.log('WebSocket unavailable, falling back to HTTP polling');
      this.fallbackToHttpPolling(this.props.token);
    } else if (connectionData.status === 'error' || connectionData.status === 'disconnected') {
      // If WebSocket fails, fall back to HTTP polling
      if (this.state.useWebSocket && connectionData.status === 'error') {
        console.log('WebSocket error, falling back to HTTP polling');
        this.fallbackToHttpPolling(this.props.token);
      }
    }
  };

  getNotifications = async () => {
    if (!this.props.active) {
      this.setState({ notificationLoading: false });
      return;
    }
    const token = this.props.token;
    if (!token || typeof token !== 'string' || token === 'undefined') {
      console.log('No valid token found for notifications API call.');
      this.setState({ notificationLoading: false });
      return;
    }
    try {
      // Only log for HTTP polling, not for WebSocket initial load
      if (!this.state.useWebSocket || this.state.wsStatus !== 'connected') {
        console.log('Making notifications API call with token:', token.substring(0, 20) + '...');
      }

      const apiResponse = await NotificationsApi.getNotifications(token);

      if (apiResponse && apiResponse.success && apiResponse.data) {
        const response = apiResponse.data;

        // Filter out read notifications if total count is over 5
        let filteredItems = response.Items || [];
        if (response.Count > 5) {
          filteredItems = filteredItems.filter(item => !item.isRead);
          console.log(`Filtered notifications: ${response.Items.length} -> ${filteredItems.length} (removed read notifications)`);
        }

        // Sort notifications by creation date (newest first)
        filteredItems.sort((a, b) => b.createdAt - a.createdAt);

        const filteredResponse = {
          ...response,
          Items: filteredItems,
          Count: filteredItems.length
        };

        let unreadCount = filteredItems.filter(item => !item.isRead).length;
        localStorage.setItem("notifications", JSON.stringify(filteredResponse));
        localStorage.setItem("notificationNumber", unreadCount);
        localStorage.setItem("totalNotifications", filteredResponse.Count);
        this.setState({
          notifications: filteredResponse,
          notificationNumber: unreadCount,
          notificationLoading: false,
          totalNotifications: filteredResponse.Count,
        });
        console.log('Notifications loaded successfully:', filteredItems.length, 'items,', unreadCount, 'unread');
      } else if (apiResponse && !apiResponse.success) {
        console.warn('Notifications API failed:', apiResponse.message);
        // Don't show error toast if it's just no notifications or endpoint not found
        if (apiResponse.status !== 404) {
          toast.warn(`Failed to load notifications: ${apiResponse.message}`);
        }
        this.setState({ notificationLoading: false });
      } else {
        console.warn('Invalid notifications response:', apiResponse);
        this.setState({ notificationLoading: false });
      }
    } catch (err) {
      console.error('Failed to get notifications:', err);
      // Only show error if it's not a network or endpoint issue
      if (!err.message?.includes('404') && !err.message?.includes('NetworkError')) {
        toast.warn(`Failed to get notifications: ${err.message || err}`);
      }
      this.setState({ notificationLoading: false });
    }
  };

  handleNotificationClick = (event) => {
    this.setIsReadNotifications();
    this.setState({ notificationAnchorEl: event.currentTarget, notificationNumber: 0 });
  };

  handleNotificationClose = () => {
    this.setState({ notificationAnchorEl: null });
  };

  locallyRead = (i) => {
    let notifs = this.state.notifications;
    notifs.Items[i].isRead = true;
    this.setState({ notifications: notifs });
  };

  setIsReadNotifications = async () => {
    const token = this.props.token;
    if (!token || typeof token !== 'string' || token === 'undefined') {
      console.log('No valid token found for notifications update API call.');
      return;
    }
    try {
      const updateData = { type: "isRead", data: "true" };
      await NotificationsApi.updateNotifications(token, updateData);
      console.log('Notifications marked as read');
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    }
  };

  toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    this.setState({ drawerOpen: open });
  };

  analyze = async (ev) => {
    let file = ev.target.files[0];
    if (!file) return;

    // Validate file type for analyze (similar to collection uploads)
    const validTypes = [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/bmp', 
      'application/zip', 
      'video/mp4', 
      'video/quicktime', 
      'video/x-msvideo', 
      'video/x-matroska'
    ];
    if (!validTypes.includes(file.type)) {
      toast.warn('Please select a valid media file (JPG, PNG, BMP, MP4) or ZIP archive.');
      ev.target.value = null;
      return;
    }

    this.setState({ uploadPhotoToAnalyze: true });

    // Validate token first
    const token = this.props.token;
    if (!token || typeof token !== 'string' || token === 'undefined') {
      toast.warn('No valid token found for analyze API call.');
      this.setState({ uploadPhotoToAnalyze: false });
      ev.target.value = null;
      return;
    }

    try {
      // Upload file to S3 with proper error handling
      const s3 = new S3Client({
        region: "us-east-1",
        credentials: this.props.AWS?.config?.credentials
      });

      // Convert file to ArrayBuffer to avoid stream reader issues (same as collectionDetails.jsx)
      const fileBuffer = await file.arrayBuffer();

      const params = {
        Bucket: TEMP_BUCKET,
        Key: `analyze/${this.props.user.sub}/${file.name}`,
        Body: new Uint8Array(fileBuffer),
        ContentType: file.type,
        ServerSideEncryption: "AES256",
      };

      console.log('Uploading file to S3 for analysis:', file.name, 'Type:', file.type);
      await s3.send(new PutObjectCommand(params));
      console.log('S3 upload successful, triggering analysis...');

      // Use AnalyzeApi instead of direct fetch
      const analysisData = { type: "analyze", fileName: file.name };
      const apiResponse = await AnalyzeApi.addAnalysis(token, analysisData);

      if (apiResponse && apiResponse.success) {
        toast.success("Upload Successful: Check activity logs for results.");
        console.log('Analysis request successful:', apiResponse);
      } else {
        const errorData = apiResponse?.data || {};
        const code = errorData.code;
        const friendlyMessages = {
          SUBSCRIPTION_REQUIRED: 'Analysis requires an active subscription. Upgrade your plan to analyze files.',
          STORAGE_LIMIT_EXCEEDED: 'Storage limit reached. Free up space or upgrade your plan.',
          USER_NOT_FOUND: 'Account not found. Please log in again.',
          FILE_NOT_FOUND: 'Upload not found. Try re-uploading the file.',
          UNSUPPORTED_FILE_TYPE: 'Unsupported file type. Please upload JPG, PNG, BMP, MP4, or ZIP.',
        };
        const errorMessage = friendlyMessages[code] || apiResponse?.message || 'Analysis request failed';
        toast.warn(`Upload failed: ${errorMessage}`);
        console.error('Analysis API error:', apiResponse);
      }
    } catch (err) {
      console.error('Analyze upload error:', err);

      // Provide more specific error messages
      if (err.name === 'TypeError' && err.message.includes('getReader')) {
        toast.warn('Upload failed: File processing error. Please try again with a different image.');
      } else if (err.name === 'NetworkError' || err.message.includes('fetch')) {
        toast.warn('Upload failed: Network error. Please check your connection and try again.');
      } else if (err.message.includes('credentials')) {
        toast.warn('Upload failed: Authentication error. Please refresh the page and try again.');
      } else {
        toast.warn(`Upload failed: ${err.message || err}`);
      }
    } finally {
      this.setState({ uploadPhotoToAnalyze: false });
      ev.target.value = null;
    }
  };

  // Activity tracking methods for intelligent polling and auth refresh
  initializeActivityTracking = () => {
    // Track user activity events
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(event => {
      document.addEventListener(event, this.updateLastActivity, true);
    });

    // Check activity every 30 seconds
    this.activityCheckInterval = setInterval(this.checkActivityStatus, 30000);

    // Check token expiration every 5 minutes and refresh if user is active
    this.tokenCheckInterval = setInterval(this.checkTokenExpiration, 5 * 60 * 1000);

    // Disable visibility/focus tracking to prevent unwanted refreshes on tab switching
    // The token expiration check above should handle automatic refreshes when needed
    // document.addEventListener('visibilitychange', this.handleVisibilityChange);
    // window.addEventListener('focus', this.handleWindowFocus);
  };

  updateLastActivity = () => {
    this.lastActivityTime = Date.now();

    // Resume polling if it was paused
    if (this.isPollingPaused && this.intervalID === null) {
      console.log('User activity detected, resuming notification polling');
      this.isPollingPaused = false;
      this.intervalID = setInterval(this.getNotifications, 30000);
    }
  };

  checkActivityStatus = () => {
    const inactiveTime = Date.now() - this.lastActivityTime;
    const oneMinute = 60 * 1000;

    // Pause polling if user has been inactive for more than 1 minute
    if (inactiveTime > oneMinute && !this.isPollingPaused && this.intervalID) {
      console.log('User inactive for over 1 minute, pausing notification polling');
      clearInterval(this.intervalID);
      this.intervalID = null;
      this.isPollingPaused = true;
    }
  };

  handleVisibilityChange = () => {
    if (!document.hidden) {
      // Page became visible again - only refresh if user was recently active AND it's been a while since last refresh
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      const timeSinceLastRefresh = Date.now() - (this.lastAuthRefresh || 0);
      const fiveMinutes = 5 * 60 * 1000;
      const twentyMinutes = 20 * 60 * 1000;

      // Only refresh if user was active recently AND it's been over 20 minutes since last refresh
      if (timeSinceActivity < fiveMinutes && timeSinceLastRefresh > twentyMinutes) {
        console.log('Visibility change: Refreshing auth after extended period');
        this.handlePageActive();
      }
    }
  };

  handleWindowFocus = () => {
    // Window gained focus - only refresh if user was recently active AND it's been a while since last refresh
    const timeSinceActivity = Date.now() - this.lastActivityTime;
    const timeSinceLastRefresh = Date.now() - (this.lastAuthRefresh || 0);
    const fiveMinutes = 5 * 60 * 1000;
    const twentyMinutes = 20 * 60 * 1000;

    // Only refresh if user was active recently AND it's been over 20 minutes since last refresh
    if (timeSinceActivity < fiveMinutes && timeSinceLastRefresh > twentyMinutes) {
      console.log('Window focus: Refreshing auth after extended period');
      this.handlePageActive();
    }
  };

  handlePageActive = () => {
    // Update activity time
    this.updateLastActivity();

    // Only refresh authentication if it's been a while since last refresh (increased to 20 minutes)
    const now = Date.now();
    const timeSinceLastRefresh = now - (this.lastAuthRefresh || 0);
    const twentyMinutes = 20 * 60 * 1000;

    if (timeSinceLastRefresh > twentyMinutes) {
      console.log('Page active: Triggering auth refresh after 20+ minutes');
      this.refreshAuthentication();
    } else {
      console.log('Page active: Skipping auth refresh (last refresh was recent)');
    }
  };

  refreshAuthentication = async () => {
    try {
      // Clear any existing timeout
      if (this.authRefreshTimeout) {
        clearTimeout(this.authRefreshTimeout);
        this.authRefreshTimeout = null;
      }

      console.log('Refreshing authentication session due to page activity');
      this.lastAuthRefresh = Date.now();

      // For Cognito/Amplify, we can try to refresh the session
      if (typeof this.props.refreshAuth === 'function') {
        await this.props.refreshAuth();
      } else if (window.cognitoAuth && typeof window.cognitoAuth.refreshSession === 'function') {
        await window.cognitoAuth.refreshSession();
      }
    } catch (error) {
      console.warn('Failed to refresh authentication:', error);
      // Don't throw - this is a background operation
    }
  };

  // Check if token is close to expiration and refresh if user is active
  checkTokenExpiration = async () => {
    if (!this.props.token || !this.props.active) {
      return;
    }

    try {
      // Check if user has been active in the last 30 minutes
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      const thirtyMinutes = 30 * 60 * 1000;

      if (timeSinceActivity < thirtyMinutes) {
        // Decode JWT to check expiration (basic check)
        const tokenParts = this.props.token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = payload.exp - currentTime;

          // If token expires in less than 10 minutes, refresh it
          if (timeUntilExpiry < 600) {
            console.log('Token expires soon, refreshing...');
            await this.refreshAuthentication();
          }
        }
      }
    } catch (error) {
      console.warn('Failed to check token expiration:', error);
    }
  };

  cleanupActivityTracking = () => {
    // Remove event listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(event => {
      document.removeEventListener(event, this.updateLastActivity, true);
    });

    // Cleanup visibility and focus event listeners - DISABLED to prevent unwanted refreshes
    // document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    // window.removeEventListener('focus', this.handleWindowFocus);

    // Clear intervals
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }

    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    if (this.authRefreshTimeout) {
      clearTimeout(this.authRefreshTimeout);
      this.authRefreshTimeout = null;
    }
  };

  static getNavBarItems(accountType) {
    // Uploader accounts do NOT manage tiers/emails/distributions/store
    const showRestricted = accountType !== 'uploader';
    return [
      { id: "Dashboard", href: "dashboard", active: false },
      { id: "Collections", href: "collections", active: false },
      ...(showRestricted ? [{ id: "Tiers", href: "tiers", active: false }] : []),
      ...(showRestricted ? [{ id: "Emails", href: "emails", active: false }] : []),
      { id: "Activities", href: "activity", active: false },
      ...(showRestricted ? [{ id: "Distributions", href: "distributions", active: false }] : []),
      ...(showRestricted ? [{ id: "Store", href: "store", active: false }] : []),
      { id: "Settings", href: "settings", active: false },
      { id: "Linked Accounts", href: "linked-accounts", active: false },
    ];
  }

  render() {
    const { sidebars, link, uploadPhotoToAnalyze, drawerOpen, notificationAnchorEl, notificationNumber, notificationLoading, notifications } = this.state;
    const { isAuthenticated, user, active, logout, isRegistered, currentStorage, maxStorage, accountType } = this.props;
    const open = Boolean(notificationAnchorEl);

    // Calculate storage percentage for mobile drawer
    const storagePercent = maxStorage ? Math.round((currentStorage / maxStorage) * 100) : 0;
    const storageGb = currentStorage ? (currentStorage / 1073741824).toFixed(2) : '0.00';
    const storageGbLimit = maxStorage ? (maxStorage / 1073741824).toFixed(0) : '0';
    const showStorage = Boolean(maxStorage);

    // Enhanced mobile drawer with modern design
    const getDrawerItems = (accountType) => {
      // Uploader accounts do NOT manage tiers/emails/distributions/store
      const showRestricted = accountType !== 'uploader';
      return [
        { id: "Dashboard", href: "dashboard", icon: "🏠" },
        ...(showRestricted ? [{ id: "Tiers", href: "tiers", icon: "📋" }] : []),
        { id: "Collections", href: "collections", icon: "📷" },
        ...(showRestricted ? [{ id: "Emails", href: "emails", icon: "✉️" }] : []),
        ...(showRestricted ? [{ id: "Distributions", href: "distributions", icon: "🚚" }] : []),
        ...(showRestricted ? [{ id: "Store", href: "store", icon: "🏪" }] : []),
        { id: "Activities", href: "activity", icon: "📝" },
        { id: "Settings", href: "settings", icon: "⚙️" },
        { id: "Linked Accounts", href: "linked-accounts", icon: "🤝" },
      ];
    };
    const sideBarItems = getDrawerItems(accountType);

    const drawer = (
      <Box
        sx={{
          width: 280,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper'
        }}
        role="presentation"
      >
        {/* Header with user info */}
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
            {user ? user.user_metadata.username : 'Guest'}
          </Typography>
          {user && (
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {user.email}
            </Typography>
          )}
        </Box>

        {/* Navigation Items */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List sx={{ px: 1, py: 2 }}>
            {getDrawerItems(accountType).map((item) => (
              <ListItem
                key={item.id}
                disablePadding
                sx={{ mb: 0.5 }}
              >
                <ListItemButton
                  component={Link}
                  to={`/${item.href}`}
                  onClick={this.toggleDrawer(false)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box sx={{
                    minWidth: 40,
                    fontSize: '1.2rem',
                    mr: 1.5
                  }}>
                    {item.icon}
                  </Box>
                  <ListItemText
                    primary={item.id}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: 500
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Storage Progress and Bottom Actions */}
        <Box sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.default'
        }}>
          {showStorage && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Storage Usage
              </Typography>
              <LinearProgress
                variant="determinate"
                value={storagePercent}
                sx={{
                  width: "100%",
                  height: "6px",
                  mb: 1,
                  borderRadius: 3,
                  backgroundColor: 'divider',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3
                  }
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {storageGb} GB
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {storageGbLimit} GB
                </Typography>
              </Box>
            </>
          )}

          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={(event) => {
              this.toggleDrawer(false)(event);
              logout();
            }}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem'
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    );

    return (
      <AppBar position="sticky" color="default" id="navbar" sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={this.toggleDrawer(true)}
              sx={{ mr: 2, display: { lg: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Link to={link} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box
                component="img"
                src={LogoIcon}
                alt="ShieldShot"
                sx={{ height: 30, width: "auto", display: "block", mr: 1 }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{ fontFamily: "Jost, sans-serif", fontWeight: 400, color: "text.primary", letterSpacing: "-0.5px", lineHeight: 1, display: "flex", alignItems: "center", mt: "3px" }}
              >
                Shield
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Shot
                </Box>
              </Typography>
              {process.env.NODE_ENV !== "production" && (
                <Chip
                  label="Beta"
                  color="primary"
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: "#2997ff",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Link>
          </Box>
          
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={this.toggleDrawer(false)}
            sx={{
              '& .MuiDrawer-paper': {
                boxShadow: 3,
                borderRadius: '0 16px 16px 0',
                overflow: 'hidden'
              }
            }}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
          >
            {drawer}
          </Drawer>

          <input
            id="analyzeButtonNavBar"
            type="file"
            name="file"
            onChange={this.analyze}
            style={{ display: 'none' }}
          />

          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {notificationLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <IconButton color="inherit" onClick={this.handleNotificationClick}>
                    <Badge badgeContent={notificationNumber} color="error">
                      {active ? <NotificationsIcon /> : <BedIcon />}
                    </Badge>
                  </IconButton>
                  {/* WebSocket connection indicator */}
                  {this.state.useWebSocket && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: this.state.wsStatus === 'connected' ? 'success.main' :
                          this.state.wsStatus === 'connecting' ? 'warning.main' : 'error.main',
                        border: '1px solid white',
                        zIndex: 1
                      }}
                      title={`WebSocket: ${this.state.wsStatus}`}
                    />
                  )}
                </Box>
              )}
              <Popover
                open={open}
                anchorEl={notificationAnchorEl}
                onClose={this.handleNotificationClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Notifications
                  setHidden={this.handleNotificationClose}
                  notifications={notifications}
                  locallyRead={this.locallyRead}
                />
              </Popover>

              {isRegistered && (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  disabled={uploadPhotoToAnalyze}
                  sx={{
                    mx: 2,
                    display: { xs: 'none', lg: 'inline-flex' },
                  }}
                >
                  Analyze
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.bmp,.zip,.mp4,.mov,.avi,.mkv,image/jpeg,image/jpg,image/png,image/bmp,application/zip,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
                    onChange={this.analyze}
                  />
                </Button>
              )}
              {isRegistered && (
                <IconButton
                  component="label"
                  color="primary"
                  disabled={uploadPhotoToAnalyze}
                  sx={{
                    display: { xs: 'inline-flex', lg: 'none' },
                  }}
                >
                  <ImageIcon />
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.bmp,.zip,.mp4,.mov,.avi,.mkv,image/jpeg,image/jpg,image/png,image/bmp,application/zip,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
                    onChange={this.analyze}
                  />
                </IconButton>
              )}
              {uploadPhotoToAnalyze && <CircularProgress size={20} />}

              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  logout();
                }}
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
              >
                Log out
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
    );
  }
}

export default NavBar;
